import { LDSAvatar, LDSCheckbox, LDSHeading } from "@resmed/lumina-design-system";

export interface ParticipantListItemProps {
  displayName: string;
  roleName: string;
  avatar?: string | null;
  showCheckbox?: boolean;
  checked?: boolean;
  onToggle?: () => void;
  disabled?: boolean;
  borderless?: boolean;
  inlineDetails?: boolean;
}

const ParticipantListItem = ({
  displayName,
  roleName,
  avatar,
  showCheckbox = false,
  checked = false,
  onToggle,
  disabled = false,
  borderless = false,
  inlineDetails = false,
}: ParticipantListItemProps) => {
  const fallbackLabel = (displayName || "U").charAt(0).toUpperCase();
  const isInteractive = showCheckbox && !disabled;

  return (
    <div
      role={showCheckbox ? "checkbox" : undefined}
      aria-checked={showCheckbox ? checked : undefined}
      aria-disabled={disabled || undefined}
      tabIndex={isInteractive ? 0 : undefined}
      onClick={
        isInteractive
          ? (event) => {
              event.stopPropagation();
              onToggle?.();
            }
          : undefined
      }
      onKeyDown={
        isInteractive
          ? (event) => {
              if (event.key === " " || event.key === "Enter") {
                event.preventDefault();
                event.stopPropagation();
                onToggle?.();
              }
            }
          : undefined
      }
      className={[
        "lds-flex lds-items-center lds-gap-3",
        borderless
          ? "lds-bg-transparent lds-p-1"
          : "lds-rounded-lg lds-border lds-border-bordercolor-default lds-bg-white lds-p-3",
        isInteractive ? "lds-cursor-pointer hover:lds-bg-list-row-hover lds-select-none" : "",
        disabled ? "lds-opacity-60" : "",
      ].join(" ")}
    >
      {showCheckbox && (
        // stopPropagation prevents the parent row's onClick from double-firing
        <LDSCheckbox
          id={`participant-checkbox-${displayName.replace(/\s+/g, "-")}`}
          checked={checked}
          disabled={disabled}
          onChange={() => !disabled && onToggle?.()}
          onClick={(e) => e.stopPropagation()}
          rootClassName="!lds-w-4 !lds-h-4"
          boxClassName="!lds-w-4 !lds-h-4"
        />
      )}
      <LDSAvatar
        size="small"
        shape="square"
        image={avatar || undefined}
        imageFallback="default"
        label={fallbackLabel}
        rootClassName="lds-h-10 lds-w-10 lds-min-w-10 lds-shrink-0 lds-rounded"
      />

      <div
        className={[
          "lds-min-w-0 lds-flex-1",
          inlineDetails ? "lds-flex lds-items-center lds-gap-[10px]" : "",
        ].join(" ")}
      >
        <LDSHeading
          size="h5"
          className="lds-truncate lds-font-sans lds-text-base lds-font-semibold lds-leading-6 lds-text-[#09090B]"
        >
          {displayName}
        </LDSHeading>
        <LDSHeading
          size="h5"
          className="lds-truncate lds-font-sans lds-text-base lds-font-normal lds-leading-6 lds-text-[#09090B]"
        >
          {roleName}
        </LDSHeading>
      </div>
    </div>
  );
};

export default ParticipantListItem;
